import { MB_BlurProps } from '@/components/general/blur';
import React, { memo, useCallback, useState } from 'react';
import { StyleProp, TextStyle, ViewStyle } from 'react-native';
import { MB_PopUpUI } from './MB_PopUpUI';
import { MB_ModalAnimationProps } from '@/components/general/modal';

interface MB_PopUpRef {
  show: (props: MB_ShowPopUp) => void;
  hide: () => void;
}

type TPopUpRefObj = {
  current: MB_PopUpRef | null;
};

interface IPopUpState extends MB_ShowPopUp {
  isVisible: boolean;
}

interface MB_ShowPopUp {
  /**
   * Text to show in the title of the pop-up.
   */
  title?: string;

  /**
   * Style to apply to the title text.
   */
  titleStyle?: StyleProp<TextStyle>;

  /**
   * Text to show in the message of the pop-up.
   */
  message?: string;

  /**
   * Style to apply to the message text.
   */
  messageStyle?: StyleProp<TextStyle>;

  /**
   * Component to show at the top of the pop-up.
   */
  topComponent?: React.ReactNode;

  /**
   * Component to show in the middle of the pop-up.
   */
  middleComponent?: React.ReactNode;

  /**
   * Style to apply to the container of the pop-up.
   */
  containerStyle?: StyleProp<ViewStyle>;

  /**
   * Text to show in the button of the pop-up.
   */
  buttonText?: string;

  /**
   * Action to perform when the button is pressed.
   */
  buttonAction?: () => void | Promise<void>;

  /**
   * Style to apply to the button.
   */
  buttonStyle?: {
    /**
     * Style to apply to the container of the button.
     */
    container?: StyleProp<ViewStyle>;

    /**
     * Style to apply to the text of the button.
     */
    text?: StyleProp<TextStyle>;
  };

  /**
   * Text to show in the secondary button of the pop-up.
   */
  secondaryButtonText?: string;

  /**
   * Action to perform when the secondary button is pressed.
   */
  secondaryButtonAction?: () => void | Promise<void>;

  /**
   * Style to apply to the secondary button.
   */
  secondaryButtonStyle?: {
    /**
     * Style to apply to the container of the secondary button.
     */
    container?: StyleProp<ViewStyle>;

    /**
     * Style to apply to the text of the secondary button.
     */
    text?: StyleProp<TextStyle>;
  };

  /**
   * Props to pass to the `MB_Blur` component.
   */
  blurProps?: MB_BlurProps;
  animationProps?: MB_ModalAnimationProps;
}

let mbShowPopUp = (props: MB_ShowPopUp) => {
  getRef()?.show(props);
};

let mbHidePopUp = () => {
  getRef()?.hide();
};

let refs: TPopUpRefObj[] = [];

/**
 * Adds a ref to the end of the array, which will be used to show the MB_PopUps until its ref becomes null.
 *
 * @param newRef the new ref, which must be stable for the life of the MB_PopUp instance.
 */
function addNewRef(newRef: MB_PopUpRef) {
  refs.push({
    current: newRef,
  });
}

/**
 * Removes the passed in ref from the file-level refs array using a strict equality check.
 *
 * @param oldRef the exact ref object to remove from the refs array.
 */
function removeOldRef(oldRef: MB_PopUpRef | null) {
  refs = refs.filter(r => r.current !== oldRef);
}

/**
 * Get the active MB_PopUp instance `ref`, by priority.
 * The "highest" MB_PopUp in the `View` hierarchy has the highest priority.
 *
 * For example, a MB_PopUp inside a `Modal`, would have had its ref set later than a MB_PopUp inside App's Root.
 * Therefore, the library knows that it is currently visible on top of the App's Root
 * and will thus use the `Modal`'s MB_PopUp when showing/hiding.
 *
 * ```js
 * <>
 *  <MB_PopUp />
 *  <Modal>
 *    <MB_PopUp />
 *  </Modal>
 * </>
 * ```
 */
function getRef() {
  const reversePriority = [...refs].reverse();
  const activeRef = reversePriority.find(ref => ref?.current !== null);

  if (!activeRef) {
    return null;
  }
  return activeRef.current;
}

const MB_PopUpRoot = React.forwardRef(({}, ref) => {
  const [popUpState, setPopUpState] = useState<IPopUpState>({ isVisible: false });

  const show = useCallback((props: MB_ShowPopUp) => {
    setPopUpState({
      isVisible: true,
      containerStyle: props.containerStyle,
      title: props.title,
      titleStyle: props.titleStyle,
      message: props.message,
      messageStyle: props.messageStyle,
      topComponent: props.topComponent,
      middleComponent: props.middleComponent,
      buttonText: props.buttonText,
      buttonAction: props.buttonAction,
      buttonStyle: props.buttonStyle,
      secondaryButtonText: props.secondaryButtonText,
      secondaryButtonAction: props.secondaryButtonAction,
      secondaryButtonStyle: props.secondaryButtonStyle,
      animationProps: props.animationProps,
    });
  }, []);

  const hide = useCallback(() => {
    setPopUpState(old => ({ ...old, isVisible: false }));
  }, []);

  // This must use useCallback to ensure the ref doesn't get set to null and then a new ref every render.
  React.useImperativeHandle(
    ref,
    React.useCallback(
      () => ({
        show,
        hide,
      }),
      [hide, show]
    )
  );

  return <MB_PopUpUI {...popUpState} onDismiss={hide} />;
});

const MB_PopUp = memo((defaultProps?: Partial<MB_ShowPopUp>) => {
  const popUpRef = React.useRef<MB_PopUpRef | null>(null);

  /*
    This must use `useCallback` to ensure the ref doesn't get set to null and then a new ref every render.
    Failure to do so will cause whichever MB_PopUp *renders or re-renders* last to be the instance that is used,
    rather than being the MB_PopUp that was *mounted* last.
  */
  const setRef = React.useCallback((ref: MB_PopUpRef | null) => {
    // Since we know there's a ref, we'll update `refs` to use it.
    if (ref) {
      // store the ref in this MB_PopUp instance to be able to remove it from the array later when the ref becomes null.
      popUpRef.current = ref;
      addNewRef(ref);
    } else {
      // remove the this MB_PopUp's ref, wherever it is in the array.
      removeOldRef(popUpRef.current);
    }
  }, []);

  return <MB_PopUpRoot ref={setRef} {...defaultProps} />;
});

export { MB_PopUp, mbHidePopUp, mbShowPopUp };
export type { MB_PopUpRef, MB_ShowPopUp };
